import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Badge from '@mui/material/Badge';
import styles from './viewJob.module.css';
import axios from '../../../../api/axios';
import { useCRUD } from '../../../../api/crud';
import { JOBS_ENDPOINT, MESSAGES_UNREAD_COUNTS_ENDPOINT } from '../../../../consts';
import { useBreadcrumbs } from '../../../../components/ui/breadcrumps/breadcrumbsContext';
import { useHasPermission } from '../../../../contexts/auth';
import { UnreadMessageCounts } from '../../../../types/messages';

export const JOB_MESSAGES_UPDATED_EVENT = 'jobMessagesUpdated';

export const ViewJob = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { setOverride, setLinkOverride, clearLinkOverride } = useBreadcrumbs();
  const { get } = useCRUD(JOBS_ENDPOINT);
  const canViewHistory = useHasPermission("analytics.view_instancehistoryrecord");
  const canViewMessages = useHasPermission("messaging.view_message");
  const canViewAllJobs = useHasPermission("service.view_job");
  const canViewOwnJobs = useHasPermission("service.view_own_job");
  const canViewEquipmentsServiceHistory = canViewAllJobs || canViewOwnJobs;
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);
  const listSearchRef = useRef((location.state as { search?: string } | null)?.search ?? '');

  const fetchUnreadCount = useCallback(async () => {
    if (!id || !canViewMessages) {
      setUnreadMessagesCount(0);
      return;
    }

    try {
      const { data } = await axios.get<{ counts: UnreadMessageCounts }>(
        MESSAGES_UNREAD_COUNTS_ENDPOINT,
        { params: { model_name: 'job', object_ids: id } }
      );
      setUnreadMessagesCount(data?.counts?.[String(id)] ?? 0);
    } catch {
      setUnreadMessagesCount(0);
    }
  }, [canViewMessages, id]);

  useEffect(() => {
    if (listSearchRef.current) {
      setLinkOverride('/service/jobs', `/service/jobs${listSearchRef.current}`);
    }
    return () => {
      clearLinkOverride('/service/jobs');
    };
  }, [clearLinkOverride, setLinkOverride]);

  useEffect(() => {
    const fetchJob = async () => {
      if (id) {
        const response = await get(id);
        setOverride(`/service/jobs/${id}`, response.data ? response.data.number || id : id);
      }
    };

    const handleJobUpdated = () => {
      fetchJob();
    };

    const handleCounterAdded = () => {
      fetchJob();
    };

    const handleMessagesUpdated = () => {
      void fetchUnreadCount();
    };

    window.addEventListener('jobUpdated', handleJobUpdated);
    window.addEventListener('equipmentCounterAdded', handleCounterAdded);
    window.addEventListener(JOB_MESSAGES_UPDATED_EVENT, handleMessagesUpdated);
    fetchJob();
    void fetchUnreadCount();

    return () => {
      window.removeEventListener('jobUpdated', handleJobUpdated);
      window.removeEventListener('equipmentCounterAdded', handleCounterAdded);
      window.removeEventListener(JOB_MESSAGES_UPDATED_EVENT, handleMessagesUpdated);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (location.pathname.includes('/messages')) {
      setUnreadMessagesCount(0);
    }
  }, [location.pathname]);

  const pathname = location.pathname;
  const jobId = id;
  const basePath = jobId ? `/service/jobs/${jobId}` : '/service/jobs';

  const visibleTabs = [
    { key: 'basic', path: basePath },
    ...(canViewHistory ? [{ key: 'history', path: `${basePath}/history` }] : []),
    ...(canViewEquipmentsServiceHistory
      ? [{ key: 'service-history', path: `${basePath}/service-history` }]
      : []),
    ...(canViewMessages ? [{ key: 'messages', path: `${basePath}/messages` }] : []),
  ];

  const getTabValue = () => {
    if (pathname.includes('/history')) {
      const index = visibleTabs.findIndex((tab) => tab.key === 'history');
      return index >= 0 ? index : 0;
    }
    if (pathname.includes('/service-history')) {
      const index = visibleTabs.findIndex((tab) => tab.key === 'service-history');
      return index >= 0 ? index : 0;
    }
    if (pathname.includes('/messages')) {
      const index = visibleTabs.findIndex((tab) => tab.key === 'messages');
      return index >= 0 ? index : 0;
    }

    return 0;
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    const selectedTab = visibleTabs[newValue];
    if (selectedTab) {
      navigate(selectedTab.path);
    } else {
      navigate(basePath);
    }
  };

  const renderTabLabel = (tabKey: string, label: string) => {
    if (tabKey === 'messages' && unreadMessagesCount > 0) {
      return (
        <Badge badgeContent={unreadMessagesCount} color="error" max={99}>
          <Box component="span" sx={{ pr: 1 }}>
            {label}
          </Box>
        </Badge>
      );
    }
    return label;
  };

  return (
    <Box className={styles.container}>
      <Tabs
        value={getTabValue()}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
      >
        {visibleTabs.map((tab) => {
          const labels: Record<string, string> = {
            basic: t('Basic info'),
            'service-history': t('Service History'),
            history: t('Job History'),
            messages: t('Messages'),
          };

          return (
            <Tab
              key={tab.key}
              label={renderTabLabel(tab.key, labels[tab.key])}
            />
          );
        })}
      </Tabs>
      <Box className={styles.content}>
        <Outlet />
      </Box>
    </Box>
  );
};
